// src/pages/08_admin/AdminDashboardStyle.js
import styled from "styled-components";
import {
  MainHeading,
  MainSection,
  MainWrapper,
} from "../../components/01_config/MainStyles";
import { colors } from "../../components/01_config/Colors";

export const Wrapper = styled(MainWrapper)``;

export const Section = styled(MainSection)`
  flex-direction: column;
`;

export const Title = styled(MainHeading)``;

export const TableWrapper = styled.div`
  width: 100%;
  max-width: 1000px;
  margin: 30px auto;
  padding: 20px;
  background-color: rgba(255, 255, 255, 0.6);
  box-shadow: 0px 0px 10px #d6c9c9;
  border-radius: 10px;
  box-sizing: border-box;

  @media (max-width: 700px) {
    margin: 16px auto;
    padding: 12px;
    border-radius: 12px;
  }
`;

export const ControlsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

export const FiltersGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;

  span {
    font-size: 0.9rem;
    color: ${colors.greyText || "#444"};
    white-space: nowrap;
  }

  @media (max-width: 700px) {
    width: 100%;
    gap: 10px;

    span {
      width: 100%;
      white-space: normal;
      font-size: 0.85rem;
    }
  }
`;

export const SummaryGroup = styled.div`
  font-size: 0.9rem;
  color: ${colors.greyText || "#444"};
  line-height: 1.35;

  div {
    white-space: nowrap;
  }

  @media (max-width: 700px) {
    width: 100%;
    font-size: 0.85rem;

    div {
      white-space: normal;
    }
  }
`;

export const FilterSelect = styled.select`
  padding: 7px 10px;
  border-radius: 8px;
  border: 1px solid ${colors.greyFrame};
  background-color: white;
  font-size: 0.9rem;
  outline: none;
  max-width: 100%;

  &:focus {
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.06);
  }

  @media (max-width: 700px) {
    width: 100%;
  }
`;

export const FilterInput = styled.input`
  padding: 7px 10px;
  border-radius: 8px;
  border: 1px solid ${colors.greyFrame};
  font-size: 0.9rem;
  outline: none;
  max-width: 100%;

  &:focus {
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.06);
  }

  @media (max-width: 700px) {
    width: 100%;
  }
`;

/* =========================
   Desktop table styles
   ========================= */
export const UsersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  @media (max-width: 700px) {
    /* switch layout – table becomes block container */
    display: block;
  }
`;

export const UsersHeadCell = styled.th`
  text-align: left;
  padding: 10px 8px;
  border-bottom: 2px solid ${colors.greyFrame};
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.02em;

  @media (max-width: 700px) {
    display: none; /* hide headers on mobile */
  }
`;

export const UsersRow = styled.tr`
  &:nth-child(even) {
    background-color: rgba(255, 255, 255, 0.55);
  }

  @media (max-width: 700px) {
    /* make each row a “card” */
    display: block;
    background-color: rgba(255, 255, 255, 0.75);
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 12px;
    padding: 10px 12px;
    margin-bottom: 12px;
    box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.06);
  }
`;

export const UsersCell = styled.td`
  padding: 10px 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  vertical-align: middle;
  overflow-wrap: anywhere;

  @media (max-width: 700px) {
    /* each cell becomes a row: label + value */
    display: grid;
    grid-template-columns: 110px 1fr;
    gap: 10px;
    align-items: center;

    padding: 8px 0;
    border-bottom: 1px dashed rgba(0, 0, 0, 0.08);

    &:last-child {
      border-bottom: none;
    }

    /* label from data-label */
    &::before {
      content: attr(data-label);
      font-weight: 700;
      font-size: 0.78rem;
      color: ${colors.greyText || "#444"};
      text-transform: uppercase;
      letter-spacing: 0.02em;
    }

    /* make selects/badges fit */
    select,
    input {
      width: 100%;
      max-width: 100%;
    }
  }
`;

/* Optional: keep role badge for future */
export const RoleBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  background-color: ${colors.greyFrame};
  color: ${colors.greyText || "#444"};
`;

export const ActiveBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  background-color: ${({ active }) => (active ? colors.green : colors.greyFrame)};
  color: ${({ active }) => (active ? "white" : "black")};
  user-select: none;

  ${({ clickable }) =>
    clickable &&
    `
      cursor: pointer;
      &:hover { opacity: 0.92; }
      &:active { transform: scale(0.98); }
    `}
`;

export const ActionButton = styled.button`
  padding: 7px 12px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;

  background-color: ${colors.main};
  color: white;

  &:hover {
    opacity: 0.92;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  @media (max-width: 700px) {
    width: 100%;
  }
`;
